import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  Send,
  FileText,
  AlertTriangle,
  Brain,
  XCircle,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Circle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import ipvService from '../../services/ipvService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';

/**
 * 87 preguntas del Test IPV con sus 3 opciones cada una
 */
const PREGUNTAS_IPV = [
  { numero: 1, texto: 'T… debe salir de viaje con una persona de la que no conoce nada. ¿Sobre cuál de los siguientes aspectos de esa persona es preferible informar a T… para que el viaje resulte mejor?', opciones: ['Su estilo de vida.', 'Los puntos que tengan en común.', 'Su actividad y responsabilidades personales.'] },
  { numero: 2, texto: 'Entre los siguientes tipos de vendedores de prendas confeccionadas ¿Cuál es el que tiene más probabilidades de éxito?', opciones: ['El que presente las últimas novedades.', 'El que, tratando de conocer el estilo de su cliente se interese por su modo de vida.', 'El que posea una buena capacidad de convencer.'] },
  { numero: 3, texto: 'B… tiene un proyecto importante para la promoción de un nuevo producto y va a exponer su idea ante el Comité de Dirección. ¿Cuál de las siguientes cualidades le será más útil para persuadir a su auditorio?', opciones: ['Competencia técnica y un conocimiento perfecto del tema.', 'Capacidad para modificar su razonamiento según la actitud del auditorio.', 'Facultad para mantener el orden de sus ideas a pesar las interrupciones.'] },
  { numero: 4, texto: 'Se envía a G… contra su voluntad, a un país extranjero, por el cual no se siente atraído en principio, para una estancia de varias semanas. ¿Cuál será su actitud?', opciones: ['Estimar que la duración de su estancia es demasiado corta para conseguir interesarse.', 'Tratar de aprender la lengua para comprender mejor a este país y vivir más gusto en él.', 'No tener más que los contactos estrictamente necesarios para la buena marcha de su trabajo.'] },
  { numero: 5, texto: 'Según su opinión, las personas que dicen siempre "su" verdad a los demás, aunque ésta sea desagradable, lo hacen, en general porque:', opciones: ['No saben controlar sus impulsos y dicen espontáneamente lo que piensan.', 'No les gusta la hipocresía.', 'Piensan que esto simplifica las relaciones.'] },
  { numero: 6, texto: 'Un buen amigo de C… había comenzado bastante brillantemente su carrera profesional, pero los resultados no han sido los que cabría esperar y terminó teniendo numerosos fracasos ¿Qué piensa C… acerca de esto?', opciones: ['Que las condiciones no le han sido favorable.', 'Que no estaba a la altura necesaria.', 'Que no ha utilizado bien los medios para salir adelante.'] },
  { numero: 7, texto: 'B... encuentra, en casa de unos amigos, a una persona que aparenta una edad muy inferior a la que realmente tiene. ¿Qué opinara B...?', opciones: ['"Probablemente ha encontrado en la vida lo que le convenía"', 'Tiene buena suerte, es cuestión de naturaleza"', '"Seguramente ha tenido una vida fácil"'] },
  { numero: 8, texto: 'Para mejorar su estado físico, S... decide aprender judo. Después de unos meses de entrenamiento se da cuenta de que progresa muy lentamente. ¿Cuál será su reacción?', opciones: ['Pensando que realmente no está hecho para el judo, elegirá otro deporte.', 'Convencido de que no está dotado para las actividades corporales buscará otra forma de actividad.', 'Continuará sus esfuerzos con la esperanza de que seguramente serán un día coronados por el éxito.'] },
  { numero: 9, texto: 'P... se acuesta una noche muy fatigado, pero no puede dormirse porque sus vecinos del piso superior han organizado una fiesta muy ruidosa. ¿Qué hará?', opciones: ['Subir y advertir a sus vecinos.', 'Dar con la escoba algunos golpes en el techo.', 'Tomar un somnífero y tratar de dormir cueste lo que cueste.'] },
  { numero: 10, texto: 'Dado que no tiene computador, G... llega a un local de internet para enviar un mail justo a la hora de cerrar. El encargado le dice que es demasiado tarde, que va a cerrar. ¿Qué hace C...?', opciones: ['Ir a casa de un amigo donde puede poner el mail.', 'Convencer al encargado de que su mail es muy urgente y que debe salir inmediatamente.', 'Ir a un local que cierra más tarde.'] },
  { numero: 11, texto: 'X... hace un viaje de turismo por un país en que es costumbre discutir los precios. ¿Cuál será su actitud?', opciones: ['Hará pocas compras porque le molesta tener que discutir siempre.', 'Hará numerosas compras, incluso un poco inútiles, por que le encanta discutir los precios.', 'Convencido de que le timarían, preferirá los almacenes de precio fijo.'] },
  { numero: 12, texto: 'Sin tener en cuenta la formación y competencias necesarias. ¿Cuál de las siguientes direcciones y servicios le gustaría asumir.', opciones: ['Gestión.', 'Personal.', 'Publicidad.'] },
  { numero: 13, texto: 'Un escritor poco conocido acaba de recibir el premio Planeta. ¿Qué cree usted que preferirá hacer?', opciones: ['Escribir menos y aprovecharse de su éxito para salir, recibir a la gente y hacer una vida de sociedad.', 'Intentar escribir una obra maestra.', 'Dedicarse a la pintura e intentar triunfar también en ella.'] },
  { numero: 14, texto: 'Si usted se encontrara en una situación de examen en la que pudiera elegir entre los temas, ¿Cuál escogería?', opciones: ['Un tema ampliamente tratado durante el curso con el que tiene, casi la plena seguridad de alcanzar la nota necesaria.', 'Un tema que le permita, con un mínimo de conocimientos pero con mucha lógica e imaginación, obtener una calificación muy buena pero, quizás, también muy mala.', 'Un tema intermedio que combine preparación y análisis personal.'] },
  { numero: 15, texto: 'Entre las siguientes actividades de descanso físico en la naturaleza ¿Cuál prefiere usted?', opciones: ['Una vuelta por la bahía cuando el mar este agitado.', 'Un baño de sol en una playa de arena fina, en un hermoso día de verano con el aire en calma.', 'Un paseo por el campo en primavera.'] },
  { numero: 16, texto: '¿Qué es, a su juicio, lo que atrae a más gente en los viajes o estancias en el extranjero?', opciones: ['Los paisajes nuevos.', 'El descubrimiento de otra civilización artística.', 'El contacto con una población de costumbres muy diferentes.'] },
  { numero: 17, texto: 'P... camina rápidamente por la calle y parece que tiene prisa. Un joven que realiza una encuesta le detiene para hacerle unas preguntas extrañas, pero divertidas. ¿Qué hará P...?', opciones: ['Rehúsa con firmeza contestar a las preguntas.', 'Responde rápidamente, porque lo encuentra divertido.', 'Excusarse de no tener tiempo para responder, sintiendo de verdad no poder hacerlo.'] },
  { numero: 18, texto: 'L... trabaja en una empresa en que recibe muy pocas informaciones a nivel oficial. ¿Qué opina usted de ello?', opciones: ['Que será muy difícil obtener información.', 'Que con los "rumores de pasillo", eso no constituye problema.', 'Que, de hecho, es muy difícil estar informado; bastara conocer a algunas personas bien situadas.'] },
  { numero: 19, texto: 'Son las 11 de la noche. Hay numerosos vehículos estacionados en la calle, algunos sobre las aceras. P... ve desde su ventana a uno que trata de abrir la puerta de un auto; parece que no tiene llave y utiliza un destornillador. ¿Cuál será la reacción de P...?', opciones: ['Probablemente es un ratero que trata de robar. ¿Y si yo llamase a la policía?', '"Alguien ha quedado atrapado en la vereda y trata de mover un coche para salir".', '"¿Qué estará haciendo? Parece que utiliza un destornillador".'] },
  { numero: 20, texto: 'Cuando un niño ha cometido una gran tontería. ¿Cuál es, a su modo de ver, la mejor reacción?', opciones: ['Castigarle, explicándole por qué se hace.', 'Explicarle la significación de su tontería situándolo en su contexto.', 'Apelar al amor propio del niño'] },
  { numero: 21, texto: 'B... tiene varios hijos y ha decidido intervenir en la educación sexual de cada uno de ellos. ¿Qué procedimiento le parece a usted que adoptará probablemente?', opciones: ['Reflexionar sobre el asunto y preparar, con el hijo mayor, un tipo de intervención que luego mantendrá con los demás hijos.', 'Seguir su intuición o sus sentimientos.', 'Adoptar un modo de proceder distinto con cada hijo.'] },
  { numero: 22, texto: '¿Cuál es para un niño la manera más "astuta" de presentar unas calificaciones escolares poco brillantes.', opciones: ['Enseñarlas lo antes posible para librarse de ello.', 'Presentarlas el lunes a la hora del desayuno.', 'Presentarlas en el momento en que se pueda apostar, además, alguna compensación'] },
  { numero: 23, texto: 'El hijo de G... ha entrado en el despacho de su padre a pesar de que éste se lo tiene prohibido, y ha derramado un vaso sobre papeles importantes. G... furioso, le da un buen tortazo. ¿Qué pensará G... algún tiempo después?', opciones: ['Que un buen azote de vez en cuando no viene mal.', 'Que tal vez se ha excedido un poco.', 'Que es conveniente enfadarse de verdad una vez para no tener que volver a hacerlo.'] },
  { numero: 24, texto: 'Una persona con la que D... se relaciona muy frecuentemente acaba de jugarle una mala pasada. ¿Cuál será la reacción de D...?', opciones: ['No muestra ninguna, pero se da cuenta de que le será muy difícil ocultar durante mucho tiempo su resentimiento.', 'No dice nada porque piensa que el daño causado es tal vez menor del que había creído.', 'Se esfuerza en ocultar su resentimiento para no entorpecer sus futuras relaciones.'] },
  { numero: 25, texto: 'N... asiste a una comida. Se da cuenta de que, a causa de una opinión que se le ha escapado, acaba de provocar una situación tensa en el grupo. ¿Qué cree usted que será para él lo más desagradable?', opciones: ['El sentimiento de que algo va a dificultar notablemente el contacto.', 'El pensamiento de que se le va a juzgar a causa de esta observación.', 'El disgusto de haber hablado sin pensar.'] },
  { numero: 26, texto: 'P... está jugando a las cartas con sus amigos, a pesar de sus esfuerzos, pierde varias veces seguidas. ¿Cuál es, según usted, su reacción más probable?', opciones: ['"Después de todo, esta tarde he perdido bastante. Seguramente ganaré la próxima vez".', '"Siempre ocurre lo mismo; el juego de las cartas no se me da bien".', '"Realmente no estoy en forma esta tarde".'] },
  { numero: 27, texto: 'R... está sentado en un cine al lado de personas que no se callan y hacen frecuentes comentarios. ¿Qué hará R...?', opciones: ['Rogarles que se callen', 'Cambiar de lugar', 'Concentrarse más en la película para evitar la molestia.'] },
  { numero: 28, texto: 'T... trabaja en una gran empresa. Ha efectuado algunos cambios en la organización de trabajo y es muy criticado por gran parte de sus colegas. ¿Cuál será la reacción de T...?', opciones: ['Tratar de justificar su posición ante sus colegas.', 'Pensar que, en cualquier caso, nunca se consigue la unanimidad.', 'Replantearse el problema.'] },
  { numero: 29, texto: '¿Qué piensa usted cuando ve a un niño enfrentarse a los adultos?', opciones: ['Está mal educado.', 'Probablemente tiene serias dificultades en sus relaciones con los demás.', 'Posee una fuerte personalidad.'] },
  { numero: 30, texto: 'M... debe contratar a un colaborador que se encargará de la dirección de su servicio. Teniendo en cuenta que tendrá responsabilidades jerárquicas, ¿a cual de las cualidades siguientes dará M... más importancia?', opciones: ['Tener autoridad.', 'Ser convincente.', 'Ser justo.'] },
  { numero: 31, texto: 'Entre los siguientes estilos de conducir un automóvil en carretera, ¿cuál es, a su parecer, el más frecuente adoptado por los automovilistas?', opciones: ['Pensando en la mecánica y en la seguridad, apenas fuerzan la velocidad de su vehículo.', 'Estimando que es tan peligroso circular demasiado despacio como demasiado a prisa, adelantan a los vehículos que les obligan a ir lentamente.', 'Toleran de mala gana no tener vía libre y adelantan sistemáticamente a los vehículos que van delante de ellos.'] },
  { numero: 32, texto: '¿Qué es lo que más valora cuando usted va a un restaurante?', opciones: ['Una buena cocina.', 'Un ambiente agradable.', 'La posibilidad de comer el plato especial de la casa.'] },
  { numero: 33, texto: 'Una señora va de compras a un almacén que se encuentra bastante lejos de su casa. A la puerta del almacén se da cuenta de que ha olvidado su monedero y su talonario de cheques. Sin tener en cuenta el tiempo que dispone, ¿Qué piensa usted que hará?', opciones: ['Volver a su casa a buscar lo que ha olvidado.', 'Ver sí conoce a alguien por la zona que pueda prestarle dinero o tratar de que le vendan a crédito.', 'Dejar las compras para otra ocasión y aprovecha la oportunidad para dar un paseo por el barrio.'] },
  { numero: 34, texto: 'M... está soltero. Ha tenido un año de trabajo muy fuerte y debe tomar una decisión sobre sus vacaciones. ¿Qué elegirá?', opciones: ['Pasar un mes en la costa para poder leer, caminar y descansar.', 'Visitar una región del país que no conoce.', 'Hacer una larga marcha a pie o en bicicleta porque piensa que el deporte es el mejor de los descansos.'] },
  { numero: 35, texto: '¿Cuál es, a su modo de ver, la mejor manera de descansar durante el fin de semana para un hombre que trabaja todos los demás días?', opciones: ['Reunirse con los amigos.', 'Ir al cine.', 'Leer.'] },
  { numero: 36, texto: 'Si usted dispusiera de un año de libertad y de los medios necesarios para hacer lo que quisiera, ¿cuál elegiría entre las actividades siguientes?', opciones: ['Dar la vuelta al mundo en solitario a borde de un velero.', 'Participar en una carrera automovilística Madrid-Bombay.', 'Visitar el mayor número posible de países.'] },
  { numero: 37, texto: 'Un empleado administrativo, incorporado recientemente a la empresa, pide a X..., uno de sus compañeros, que le preste una pequeña suma de dinero hasta el mes siguiente. ¿Qué hará X...?', opciones: ['Le prestará el dinero sin hacerle ninguna pregunta para no ponerle en un aprieto.', 'Le prestará el dinero porque a él le gustaría que le hicieran lo mismo si lo necesitara.', 'No le dará el dinero, considerando que su compañero tiene, probablemente, buenos amigos dispuestos a ayudarle.'] },
  { numero: 38, texto: 'B... se ha puesto, sin quererlo, fuera de la legalidad y debe escoger a un abogado para que le defienda. ¿Sobre cual de los criterios siguientes basará su elección?', opciones: ['Su reputación de elocuencia.', 'Sus buenas relaciones con la magistratura.', 'Su perspicacia psicológica.'] },
  { numero: 39, texto: 'E... ha sido nombrado recientemente para un puesto que implica responsabilidades jerárquicas importantes. ¿Qué actitud tomará probablemente frente a sus subordinados?', opciones: ['Modificar su forma de mandar según las personas y las circunstancias.', 'Fijarse un sistema de mando bien elaborado e idéntico para todos.', 'Basándose en la experiencia, dejarse guiar por su intuición.'] },
  { numero: 40, texto: 'R... tiene que hablar sobre informática a empleados que no tienen ningún conocimiento en este campo. ¿Cuál de las siguientes alternativas le planteará menos problemas?', opciones: ['Utilizar un vocabulario comprensible para todo el mundo.', 'Interesar al auditorio y mantener su atención.', 'Hacer participar a todos los asistentes.'] },
  { numero: 41, texto: 'L... asiste a un partido de fútbol entre dos equipos muy conocidos. El partido es muy disputado y los dos equipos rinden al máximo. Entre los espectadores el entusiasmo es tremendo:', opciones: ['L... se contenta con aplaudir las jugadas más brillantes.', 'L... es dominado por la agitación general: grita y gesticula como los otros.', 'L... discute con sus vecinos sobre las jugadas de los futbolistas.'] },
  { numero: 42, texto: 'B... atraviesa la calle por un paso de peatones con el semáforo en rojo. Un auto aparece a gran velocidad y se detiene repentinamente a pocos centímetros de B... ¿Cuál va a ser la reacción inmediata de éste?', opciones: ['Hará una observación irónica al mismo tiempo que le maldice interiormente al conductor.', 'Dará airadamente su opinión al conductor sobre su manera de conducir.', 'Lanzará al conductor algunos insultos que no puede reprimir.'] },
  { numero: 43, texto: 'A su modo de ver, ¿cuál es, entre los siguientes aspectos, el más desatendido actualmente en la educación de su hijo?', opciones: ['La aceptación de responsabilidades.', 'El gusto por el trabajo bien hecho.', 'La capacidad de desenvolverse solo rápidamente.'] },
  { numero: 44, texto: 'T... acaba de fracasar en un examen. Uno de sus amigos le dice que algunos fracasos son útiles en la vida. ¿Qué responderá él?', opciones: ['"Se ve que no estás en mi lugar, yo preferiría no pasar por esto".', '"Puede que tengas razón".', '"Admite, por lo menos, que cuando ocurre es difícil de aceptar".'] },
  { numero: 45, texto: 'L... se encuentra en una reunión amistosa. Sabe muy bien que la mayor parte de las personas presentes tienen opiniones muy diferentes a las suyas. ¿Qué piensa usted que hará?', opciones: ['Evitar toda discusión.', 'Provocar la discusión.', 'Dar su opinión al no poder evitarlo.'] },
  { numero: 46, texto: '¿Qué cualidad le parece más útil en las relaciones sociales?', opciones: ['La tolerancia.', 'La sinceridad.', 'La aceptación de compromisos.'] },
  { numero: 47, texto: 'M... acaba de modificar totalmente sus apartamento e invita a sus amigos a la inauguración. Entre los cumplidos que le hagan ¿Cuál será para él más agradable?', opciones: ['Una felicitación sobre su buen gusto.', 'Un elogio sobre la forma en que ha sacado partido de la disposición de las habitaciones.', 'Una alabanza a su originalidad, porque su decoración no se parece a ninguna otra ya vista.'] },
  { numero: 48, texto: 'S... llega en el momento de una discusión entre dos personas a las que conoce poco. El tema del que hablan, muy especifico, le es totalmente desconocido y apenas le interesa. ¿Cuál será su reacción?', opciones: ['Interviene para informarse, aún a riesgo de parecer ignorante.', 'Trata de interesarse porque no quiere parecer incorrecto', 'No interviene, porque no puede aportar un punto de vista interesante sobre el asunto.'] },
  { numero: 49, texto: 'R... llega con retraso a una reunión de copropietarios. ¿Qué es lo que probablemente le molestará más?', opciones: ['La idea de que hayan podido aprovecharse de su ausencia para tomar una decisión con la que él no esté de acuerdo.', 'El temor de tener dificultad para integrarse en la discusión.', 'La perspectiva de parecer descortés.'] },
  { numero: 50, texto: 'Se ha pedido a F... un informe lo más objetivo posible sobre su modo de ser. ¿Quién decidirá el que lo haga?', opciones: ['Su mujer.', 'Su grupo de amigos.', 'El mismo.'] },
  { numero: 51, texto: 'X... quiere construir una casa. ¿Cuál de las siguientes soluciones elegirá?', opciones: ['Llamar a un arquitecto.', 'Hacer los planes él mismo y trabajar directamente con un constructor.', 'Hacer los planos con un amigo que acaba de hacer una casa.'] },
  { numero: 52, texto: 'T... es un hombre de negocios generalmente muy ocupado. Una cita anulada a última hora le permite tener una mañana libre totalmente imprevista ¿Cómo cree usted que la utilizara?', opciones: ['Aprovechará para levantarse pronto y pasar la mañana practicando su deporte favorito.', 'Hará el balance de la semana que acaba de pasar.', 'Constituye para él una ocasión muy poco habitual de poder "perder su tiempo" y la aprovechará'] },
  { numero: 53, texto: 'D... va a ir a un país del que no conoce nada. Para preparar su viaje, ¿Qué cree usted que hará?', opciones: ['Leer libros y guías sobre ese país.', 'Preguntar a amigos o parientes que conocen bien el país.', 'Esperar a estar en el país ara ver e informarse.'] },
  { numero: 54, texto: 'Una persona cae de repente sobre la acera a las seis de la tarde, momento en que las calles están abarrotadas de gente, nadie se detiene a ofrecer su ayuda a la persona en dificultades ¿A que cree usted que se debe esta conducta?', opciones: ['A una total indiferencia por lo que puede ocurrir a los demás.', 'Al miedo ante una situación inesperada.', 'Al temor de ocuparse de lo que no le compete a uno.'] },
  { numero: 55, texto: 'Una señora va a consultar a un médico sobre una infección que le parece bastante banal. El médico la interroga ampliamente antes de examinarla. Ella contesta, por supuesto, a las preguntas, pero, en el fondo ¿Qué pensará?', opciones: ['"En su lugar, yo comenzaría por hacer el examen y preguntaría después".', '"El debe intentar conocer en que circunstancias he cogido esta enfermedad".', '"Está bien que me haga preguntas, pero hay algunas en las que no veo relación ninguna con lo que yo tengo".'] },
  { numero: 56, texto: 'C... tiene que contratar empleados de oficina; dispone de poco tiempo. ¿Qué método empleara\'?', opciones: ['Tener una entrevista con los candidatos.', 'Examinar sus "currículum vitae" y sus referencias.', 'Hacerles una prueba práctica en el trabajo.'] },
  { numero: 57, texto: 'El mejor delegado de personal es aquel que:', opciones: ['Es un buen negociador.', 'Está convencido de la justicia de la reivindicación.', 'Esta bien integrado con el personal.'] },
  { numero: 58, texto: 'La señora D... trata inútilmente de hacer que coma su hijo de cinco años; éste se resiste violentamente. ¿Qué hará ella?', opciones: ['Forzarle a comer considerando que el niño lo hace por capricho.', 'Hacerle un pequeño chantaje de tipo "Demuestra a Mamá que eres bueno y que la quieres".', 'Dejar al niño, pensando que comerá más en la comida siguiente.'] },
  { numero: 59, texto: 'Se habla frecuentemente del respeto a las "reglas del juego". En general, se trata:', opciones: ['De una disposición profunda, estable y claramente definida.', 'De la aceptación a desempeñar en cada instante el papel que conviene.', 'De una sumisión sincera a las costumbres de un grupo.'] },
  { numero: 60, texto: 'C... que es aficionado al bricolage, decide tapizar él mismo un sillón valioso. Cuando llega a la mitad de su trabajo se da cuenta de que corre el peligro de fracasar en su intento. ¿Cuál será su decisión?', opciones: ['Confiar el sillón a un especialista que realizará un trabajo impecable.', 'Deshacer lo que ya ha hecho y comenzar otra vez desde cero.', 'Dejar durante unos días su trabajo para reemprenderlo cuando esté en mejores condiciones.'] },
  { numero: 61, texto: 'C... va a ir con su mujer al campo durante los días de Pascua. Previendo la afluencia, ha reservado pasajes en ferrocarril. Salen de su casa a la hora justa, pero un embotellamiento en los alrededores de la estación les hace perder el tren. ¿Cómo cree usted que reaccionará?', opciones: ['"Siempre pasa lo mismo. Si hubieras estado preparada antes no hubiera ocurrido esto".', '"Se han fastidiado las vacaciones... No me parece nada viajar de pie en el tren siguiente".', '"¡Que le vamos a hacer! Voy a devolver los boletos y nos quedaremos en casa".'] },
  { numero: 62, texto: 'A su juicio, la mayoría de los fracasos son debido a:', opciones: ['Incapacidad.', 'Coincidencia de circunstancias negativas.', 'Fallos ocasionales.'] },
  { numero: 63, texto: 'F... ha encargado un frigorífico. En su ausencia, lo depositan en casa del conserje. El embalaje está estropeado, por lo cual el frigorífico ha sufrido algunos daños. F... decide hacer una gestión ante los servicios de distribución. ¿De qué forma se comportará?', opciones: ['Escribirá a los citados servicios conservando una copia de la carta.', 'Irá personalmente a reclamar.', 'Rogará a uno de sus amigos, empleado en esa casa, que intervenga.'] },
  { numero: 64, texto: 'Con ocasión de un viaje organizado, B... se encuentra con personas que no conoce. Al cabo de unos días se da cuenta de que la mayoría de las personas le tienen poca simpatía. ¿Cómo cree usted que reaccionará?', opciones: ['Leo tiene sin cuidado, porque para él lo importante es que el viaje esté bien organizado y que el guía sea eficaz.', 'Está un poco molesto y trata de hacer un esfuerzo para resultar más simpático.', 'Esto le amarga, en parte, sus vacaciones y propone que, en la próxima ocasión, se irá con sus amigos.'] },
  { numero: 65, texto: 'R... hace un viaje con algunos amigos. Una tarde el grupo se separa después de haberse puesto de acuerdo en la hora para la salida. A lo largo de esta tarde, R... descubre un monumento que le interesa mucho visitar, pero, si lo hace, le será imposible llegar a la cita. ¿Qué hará?', opciones: ['Hacer la visita porque piensa que no tendrá ya ocasión de volver a este lugar y que de todas maneras habrá otros que llegarán tarde también.', 'Renuncia a esta visita porque le parece más importante no hacer esperar a los demás.', 'Trata de encontrar a sus amigos para persuadirles de que hagan la visita con él, aún a riesgo de desorganizar la última parte del viaje.'] },
  { numero: 66, texto: '¿Cuál de estas profesiones le parece a usted la más envidiable?', opciones: ['Gerente de una pequeña empresa.', 'Director adjunto de una empresa internacional.', 'Asesor científico de alto nivel.'] },
  { numero: 67, texto: 'A causa de un accidente que ha causado la muerte de los padres de un niño pequeño, J... tiene que encargarse del niño con carácter definitivo. ¿Cuál será la actitud más probable que tendrá a este respecto?', opciones: ['Teme verse obligado a revisar su sistema de educación.', 'Confía en el buen carácter del niño.', 'Busca otras alternativas para el niño.'] },
  { numero: 68, texto: 'Un amigo propone a T... llevarle en su velero a hacer un pequeño crucero. T... nunca ha practicado la vela. ¿Qué cree usted que decidirá?', opciones: ['Aceptar, pensando que podrá desenvolverse bien.', 'Arreglárselas para hacer antes algunas horas de vela en una escuela.', 'No aceptar, ante el temor de ser una molestia para su amigo.'] },
  { numero: 69, texto: 'T... va hacia el trabajo por su camino habitual. Una furgoneta de reparto le bloquea durante un buen rato en una calle estrecha. A juicio de usted, ¿qué será lo mas desagradable para T...?', opciones: ['La perspectiva de llegar con retraso a su despacho.', 'El hecho de tener que esperar sin poder moverse ni hacer nada.', 'Los bocinazos –inútiles-- de otros automovilistas.'] },
  { numero: 70, texto: 'G... trabaja en una empresa para la cual debe efectuar frecuentes desplazamientos. Acostumbra a hacer dos horas de atletismo todas las semanas en una sesión de entrenamiento colectivo. A juicio de usted, ¿cuál es su actitud en el curso de esta sesión?', opciones: ['Insiste en ciertas especialidades que le permitan mantenerse en buena forma.', 'Realiza todas las actividades propuestas, pero sin esforzarse demasiado, porque tiene un trabajo cansado.', 'Se entrega a fondo y frecuentemente termina agotado, porque no concibe el deporte de otra manera.'] },
  { numero: 71, texto: 'Según su criterio, ¿cuál es la ventaja de tener un perro en un apartamento de la ciudad?', opciones: ['Permite sentirse menos solo.', 'Favorece la ocasión de caminar un poco cada día paseando el perro.', 'Vigila el apartamento.'] },
  { numero: 72, texto: 'En el transcurso de una reunión en que S... se ha encontrado con amigos, les propone terminar la velada en su casa. Al enterarse, se une a ellos una persona que se mantenía alejada del grupo y a la cual él estima poco. ¿Cuál será la reacción de S...?', opciones: ['Piensa que, después de todo, tendrá ocasión de conocer mejor a esta persona.', 'Estimando que se ha invitado a sí misma, no se ocupará de ella en toda la noche.', 'Lamenta haber hablado demasiado alto.'] },
  { numero: 73, texto: 'El director de un centro de delincuentes debe contratar a un nuevo educador. ¿A cual de estas cualidades dará más importancia?', opciones: ['Ser irreprochable y poder servir de modelo.', 'Tener buenos conocimientos pedagógicos.', 'Saber escuchar.'] },
  { numero: 74, texto: 'Según su opinión, ¿qué es más importante en una persona que da una fiesta?', opciones: ['Permitir a todos expresarse.', 'Invitar a personas que puedan coincidir con sus intereses.', 'Cuidar de que a nadie le falte nada.'] },
  { numero: 75, texto: 'R... ha de exponer un proyecto a un colega que no conoce. Para que R... pueda hacerse comprender por su interlocutor, es preferible que:', opciones: ['Sepa cual es su formación académica.', 'Conozca sus antecedentes profesionales.', 'Lea el informe sobre un trabajo hecho por un colega.'] },
  { numero: 76, texto: 'Si tuviera que hacer algún reproche a la profesión médica. ¿cuál escogería?', opciones: ['No consagra suficiente tiempo a cada enfermo.', 'Da demasiada importancia al dinero.', 'Utiliza a menudo un vocabulario incomprensible.'] },
  { numero: 77, texto: 'N... tiene que escoger un plan de formación para las personas de su departamento. Desearía una formación interesante para todos, pero a la vez, eficaz y rentable. ¿Qué elegiría?', opciones: ['Un plan organizado en función de las necesidades del departamento y en el que todas las fases estén definidas de antemano.', 'Un plan que tenga en cuenta a cada participante.', 'Un plan que incluya de manera regular exámenes de control.'] },
  { numero: 78, texto: 'M... acaba de tener un ataque al hígado. Sin embargo, un amigo lo llama para invitarlo a comer, precisando que le ha preparado su plato favorito. Supuesto que este plato está contraindicado para él en este momento, ¿cuál será la reacción de M...?', opciones: ['Aceptar la invitación pensando que por una vez no hará caso de su régimen.', 'Proponer que se le aplace esta invitación para más adelante cuando acabe su régimen.', 'Ir a la comida pero pedir otro plato que se ajuste a su dieta.'] },
  { numero: 79, texto: 'M... no está satisfecho con la Seguridad Social. Lo que más le desagrada es:', opciones: ['Lo absurdo de algunas disposiciones de esta entidad.', 'La gran dificultad de atenerse a la complejidad de su reglamentación.', 'La falta de atención a aspectos que él cree fundamentales.'] },
  { numero: 80, texto: 'Y... ha decidido consagrar una parte de su tiempo libre a ayudar a las personas de la tercera edad. Existen varias posibilidades. ¿Cuál elegirá en último lugar cuando no hay otra alternativa?', opciones: ['Acompañar a las personas ancianas durante el paseo.', 'Visitar a una persona mayor cada semana.', 'Hacer colectas para la construcción de un hogar de ancianos.'] },
  { numero: 81, texto: 'Entre los siguientes argumentos publicitarios, ¿cuál tiene más posibilidades de influir en el público?', opciones: ['La muestra.', 'El "slogan".', 'El testimonio de una "estrella"'] },
  { numero: 82, texto: 'Durante un viaje en tren, D... se encuentra en un compartimento abarrotado. Su vecino fuma y el humo le molesta mucho. Ya le ha rogado que dejara de fumar, sin resultado. ¿Qué hará?', opciones: ['Pedir el parecer de otros viajeros del compartimento.', 'Abrir la ventana.', 'Hablar de nuevo con su vecino con el objeto de convencerle de que deje de fumar.'] },
  { numero: 83, texto: 'El director del departamento de una empresa de productos alimenticios desea lanzar al mercado un nuevo postre. Dispone de un presupuesto que puede repartir en los tres conceptos que se citan a continuación. ¿En cual de ellos empleará más dinero?', opciones: ['En estudios de mercado para conocer los gustos de los futuros compradores.', 'En publicidad para dar a conocer su producto.', 'En la investigación de envases y presentación.'] },
  { numero: 84, texto: 'B... toma parte en una discusión muy animada, en el transcurso de la cual sus opiniones son muy atacadas. Entre las actitudes siguientes, ¿cuál será la que adopte?', opciones: ['Tratar por todos los medios de convencer a los demás.', 'Divertirse simplemente en el juego de la discusión.', 'Dejarse convencer cuando vea que sus posiciones son poco defendibles.'] },
  { numero: 85, texto: 'Cuando usted va a un restaurante, ¿qué tipo de plato prefiere?', opciones: ['Uno que ya conoce y que le gusta de modo especial.', 'La especialidad de la casa.', 'Un plato exótico que usted desconoce en absoluto.'] },
  { numero: 86, texto: 'Después de un naufragio, C... llega solo a una isla desierta. El clima es muy agradable y la vegetación le permitirá encontrar el alimento necesario. ¿Qué es, a su modo de ver, lo que probablemente le planteará menos problema?', opciones: ['El carácter nuevo e insólito de la situación.', 'La soledad.', 'La falta de algunos atractivos de la vida anterior.'] },
  { numero: 87, texto: 'J... dirige una importante firma automovilista. Puede escoger entre varias políticas comerciales. ¿Cuál elegirá?', opciones: ['Orientar sus esfuerzos para sacar de vez en cuando modelos especiales.', 'Buscar los efectos de moda y atender a una clientela particular.', 'Ofrecer de ordinario modelos clásicos.'] }
];

// 🧪 TEMPORAL: Claves de respuesta correctas del Test IPV (solo para pruebas)
const CLAVES_RESPUESTA = {
  1:'B',2:'B',3:'B',4:'B',5:'A',6:'C',7:'A',8:'B', // 8: B (invertida)
  9:'A',10:'B',11:'B',12:'C',13:'B',14:'B',15:'A',16:'C',17:'B',18:'C',
  19:'C',20:'B',21:'C',22:'C',23:'B',24:'C',25:'C',26:'B',27:'A',28:'C', // 23:B, 26:B
  29:'C',30:'B',31:'C',32:'C',33:'B',34:'C',35:'A',36:'C',37:'A',38:'C', // 31:C, 37:A
  39:'A',40:'A',41:'A',42:'A',43:'B',44:'A',45:'B',46:'B',47:'C',48:'A', // 41:A, 42:A, 43:B, 44:A
  49:'A',50:'C',51:'B',52:'A',53:'B',54:'A',55:'B',56:'A',57:'A',58:'C',
  59:'B',60:'B',61:'B',62:'A',63:'B',64:'A',65:'C',66:'A',67:'B',68:'A', // 64:A, 65:C
  69:'B',70:'C',71:'B',72:'A',73:'C',74:'A',75:'C',76:'C',77:'B',78:'B',
  79:'B',80:'C',81:'C',82:'C',83:'B',84:'A',85:'C',86:'A',87:'A' // 79:B, 81:C, 83:B, 85:C
};

/**
 * Componente para realizar el Test IPV (Inventario de Personalidad para Vendedores)
 * 87 preguntas con 3 opciones cada una (A, B, C)
 */
const RealizarExamenIpv = ({ onFinalizar, proceso }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [examen, setExamen] = useState(null);
  const [evaluacion, setEvaluacion] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [examenIniciado, setExamenIniciado] = useState(false);
  const [yaCompletado, setYaCompletado] = useState(false);
  const [preguntaActual, setPreguntaActual] = useState(1); // Nueva variable para navegación 1x1

  // Número total de preguntas IPV
  const TOTAL_PREGUNTAS = 87;

  useEffect(() => {
    verificarSiCompletado();
  }, []);

  const verificarSiCompletado = async () => {
    try {
      setLoading(true);
      
      // Verificar si el usuario ya tiene un examen IPV
      const examenesUsuario = await ipvService.getExamenesCompletadosPorUsuario(user.id);
      console.log('📋 Exámenes IPV del usuario:', examenesUsuario);
      
      // Verificar si tiene algún examen FINALIZADO
      const examenFinalizado = examenesUsuario.find(ex => ex.estado === 'FINALIZADO');
      
      if (examenFinalizado) {
        console.log('⚠️ Usuario ya completó examen IPV:', examenFinalizado);
        setYaCompletado(true);
        toast.error('Ya has completado el examen IPV anteriormente');
        return;
      }
      
      // Verificar si tiene un examen PENDIENTE (no finalizado)
      const examenPendiente = examenesUsuario.find(ex => ex.estado === 'PENDIENTE');
      
      if (examenPendiente) {
        console.log('📝 Usuario tiene examen pendiente:', examenPendiente);
        // Cargar el examen pendiente existente
        setExamen(examenPendiente);
        
        // Si tiene evaluación, cargarla
        if (examenPendiente.ipvEvaluacionId) {
          try {
            const evaluacionExistente = await ipvService.obtenerEvaluacion(examenPendiente.ipvEvaluacionId);
            setEvaluacion(evaluacionExistente);
            console.log('📝 Evaluación cargada:', evaluacionExistente);
          } catch (error) {
            console.error('Error al cargar evaluación:', error);
          }
        }
      }
      // Si no ha completado ni tiene pendiente, solo cambiar loading a false
      // NO crear el examen aquí, esperar a que el usuario presione "Iniciar Examen IPV"
    } catch (error) {
      console.error('Error al verificar examen completado:', error);
      // Si hay error al verificar, continuar normalmente
      // El usuario podrá intentar iniciar el examen
    } finally {
      setLoading(false);
    }
  };

  const iniciarExamen = async () => {
    try {
      setLoading(true);
      
      // Si ya hay un examen pendiente cargado, usar ese
      if (examen && evaluacion) {
        console.log('✅ Usando examen pendiente existente:', examen);
        setExamenIniciado(true);
        toast.success('Continuando con tu examen');
        return;
      }
      
      // Si no hay examen, crear uno nuevo
      const { examen: nuevoExamen, evaluacion: nuevaEvaluacion } = await ipvService.iniciarExamenCompleto(user.id, proceso?.id);
      
      setExamen(nuevoExamen);
      setEvaluacion(nuevaEvaluacion);
      setExamenIniciado(true);
      
      console.log('✅ Nuevo examen creado:', nuevoExamen);
      
      toast.success('Examen IPV iniciado correctamente');
    } catch (error) {
      console.error('Error al iniciar examen:', error);
      toast.error('Error al iniciar el examen IPV');
    } finally {
      setLoading(false);
    }
  };

  // 🧪 TEMPORAL: Rellena todas las respuestas con las claves correctas
  const handleResponderTodas = () => {
    setRespuestas({ ...CLAVES_RESPUESTA });
    setPreguntaActual(87);
    toast.success('✅ Todas las respuestas rellenadas con las claves correctas');
  };

  const handleSeleccionarRespuesta = (preguntaNumero, respuesta) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaNumero]: respuesta
    }));
    
    // Avanzar automáticamente a la siguiente pregunta después de seleccionar
    if (preguntaNumero < TOTAL_PREGUNTAS) {
      setTimeout(() => {
        setPreguntaActual(preguntaNumero + 1);
      }, 300); // Pequeño delay para que se vea la selección
    }
  };

  // Funciones de navegación
  const irAPregunta = (numero) => {
    if (numero >= 1 && numero <= TOTAL_PREGUNTAS) {
      setPreguntaActual(numero);
    }
  };

  const irAnterior = () => {
    if (preguntaActual > 1) {
      setPreguntaActual(preguntaActual - 1);
    }
  };

  const irSiguiente = () => {
    if (preguntaActual < TOTAL_PREGUNTAS) {
      setPreguntaActual(preguntaActual + 1);
    }
  };

  // Obtener preguntas sin responder
  const preguntasSinResponder = () => {
    const sinResponder = [];
    for (let i = 1; i <= TOTAL_PREGUNTAS; i++) {
      if (!respuestas[i]) {
        sinResponder.push(i);
      }
    }
    return sinResponder;
  };

  const calcularProgreso = () => {
    const respondidas = Object.keys(respuestas).length;
    return Math.round((respondidas / TOTAL_PREGUNTAS) * 100);
  };

  const validarRespuestasCompletas = () => {
    const respondidas = Object.keys(respuestas).length;
    return respondidas === TOTAL_PREGUNTAS;
  };

  const handleEnviarExamen = async () => {
    if (!validarRespuestasCompletas()) {
      toast.error(`Faltan ${TOTAL_PREGUNTAS - Object.keys(respuestas).length} preguntas por responder`);
      return;
    }

    setShowConfirmacion(true);
  };

  const confirmarEnvio = async () => {
    try {
      setEnviando(true);
      setShowConfirmacion(false);

      // Formatear respuestas para el backend
      const respuestasFormateadas = {};
      for (let i = 1; i <= TOTAL_PREGUNTAS; i++) {
        respuestasFormateadas[i.toString()] = respuestas[i] || '';
      }

      console.log('📝 Guardando respuestas...', respuestasFormateadas);
      
      // 1. Guardar respuestas
      const respuestaGuardada = await ipvService.guardarRespuestas(evaluacion.id, respuestasFormateadas);
      console.log('✅ Respuestas guardadas:', respuestaGuardada);
      
      // 2. Evaluar resultados (calcula puntuaciones - backend lo procesa)
      console.log('🔢 Evaluando resultados...');
      const resultadoEvaluado = await ipvService.evaluarResultados(evaluacion.id);
      console.log('✅ Resultados evaluados:', resultadoEvaluado);

      // 3. Actualizar estado del examen a FINALIZADO con evaluacionId y porcentaje
      console.log('✅ Actualizando estado del examen...');
      await ipvService.actualizarExamen(examen.id, { 
        estado: 'FINALIZADO',
        ipvEvaluacionId: evaluacion.id,
        porcentajeTest: 100
      });
      console.log('✅ Examen marcado como FINALIZADO');

      toast.success('¡Examen IPV completado exitosamente!');
      
      // Volver al dashboard después de 1.5 segundos
      setTimeout(() => {
        onFinalizar();
      }, 1500);
    } catch (error) {
      console.error('❌ Error al enviar examen:', error);
      toast.error('Error al enviar el examen. Intenta nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  // Si ya completó el examen, mostrar mensaje
  if (yaCompletado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full"
        >
          <Card className="border-t-4 border-t-red-600">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              
              <h1 className="text-3xl font-bold text-neutral-800 mb-3">
                Examen IPV Ya Completado
              </h1>
              
              <p className="text-lg text-neutral-600 mb-6">
                Ya has completado el Inventario de Personalidad para Vendedores (IPV) anteriormente.
              </p>

              <div className="bg-amber-50 rounded-lg p-6 mb-6 text-left border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">
                      Información Importante
                    </h3>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li>• Solo puedes realizar el examen IPV una vez</li>
                      <li>• Tus resultados ya han sido registrados y evaluados</li>
                      <li>• Los supervisores tienen acceso a tu evaluación completa</li>
                      <li>• Si necesitas más información, contacta al administrador</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => onFinalizar ? onFinalizar() : navigate('/postulante/tests')}
                  className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver a Mis Tests
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!examenIniciado) {
    return (
      <PantallaInicio onIniciar={iniciarExamen} />
    );
  }

  const progreso = calcularProgreso();
  const respondidas = Object.keys(respuestas).length;
  const faltantes = TOTAL_PREGUNTAS - respondidas;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header con progreso */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="border-l-4 border-l-purple-600">
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-2xl font-bold text-neutral-800 truncate">
                      Test IPV
                    </h1>
                    <p className="text-sm text-neutral-600">Examen #{examen?.id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-start sm:self-auto">
                  <Badge variant="primary" className="bg-purple-600 text-base sm:text-lg px-3 py-1.5 sm:px-4 sm:py-2">
                    {respondidas} / {TOTAL_PREGUNTAS}
                  </Badge>
                  {/* 🧪 TEMPORAL - Quitar antes de producción 
                  <button
                    onClick={handleResponderTodas}
                    title="TEMPORAL: Rellena todas las respuestas con las claves correctas"
                    className="text-xs bg-orange-500 hover:bg-orange-600 text-white font-bold px-2 py-1 rounded border-2 border-orange-700"
                  >
                    🧪 Responder Todas
                  </button>
                  */}
                </div>
              </div>

              {/* Barra de progreso */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-neutral-600">
                  <span>Progreso del examen</span>
                  <span className="font-semibold">{progreso}%</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-3 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progreso}%` }}
                    transition={{ duration: 0.3 }}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full"
                  />
                </div>
                {faltantes > 0 && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Faltan {faltantes} pregunta{faltantes !== 1 ? 's' : ''} por responder
                  </p>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Instrucciones */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="bg-blue-50 border-blue-200">
            <div className="p-4">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Instrucciones</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Lee cada afirmación cuidadosamente</li>
                <li>• Selecciona la opción que mejor te describa (A, B o C)</li>
                <li>• No hay respuestas correctas o incorrectas</li>
                <li>• Responde con sinceridad y naturalidad</li>
                <li>• Todas las preguntas deben ser respondidas</li>
              </ul>
            </div>
          </Card>
        </motion.div>

        {/* Pregunta actual - Vista 1x1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="p-3 sm:p-6">
            {/* Navegación superior */}
            <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
              <Button
                variant="outline"
                onClick={irAnterior}
                disabled={preguntaActual === 1}
                className="gap-1 sm:gap-2 px-2 sm:px-4 text-sm sm:text-base"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>
              <span className="text-sm sm:text-lg font-semibold text-neutral-700 text-center">
                <span className="hidden sm:inline">Pregunta </span>{preguntaActual}<span className="hidden sm:inline"> de</span><span className="sm:hidden">/</span> {TOTAL_PREGUNTAS}
              </span>
              <Button
                variant="outline"
                onClick={irSiguiente}
                disabled={preguntaActual === TOTAL_PREGUNTAS}
                className="gap-1 sm:gap-2 px-2 sm:px-4 text-sm sm:text-base"
                size="sm"
              >
                <span className="hidden sm:inline">Siguiente</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Pregunta actual */}
            <AnimatePresence mode="wait">
              <motion.div
                key={preguntaActual}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.2 }}
              >
                <PreguntaIPV
                  numero={preguntaActual}
                  respuestaSeleccionada={respuestas[preguntaActual]}
                  onSeleccionar={(respuesta) => handleSeleccionarRespuesta(preguntaActual, respuesta)}
                />
              </motion.div>
            </AnimatePresence>
          </Card>
        </motion.div>

        {/* Indicador de preguntas - Grid visual */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6"
        >
          <Card className="p-3 sm:p-4">
            <h3 className="font-semibold text-neutral-700 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Circle className="w-3 h-3 sm:w-4 sm:h-4" />
              Navegación de preguntas
            </h3>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              {Array.from({ length: TOTAL_PREGUNTAS }, (_, i) => i + 1).map((numero) => (
                <button
                  key={numero}
                  onClick={() => irAPregunta(numero)}
                  className={`
                    w-7 h-7 sm:w-9 sm:h-9 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all duration-200
                    flex items-center justify-center
                    ${preguntaActual === numero 
                      ? 'bg-purple-600 text-white ring-2 ring-purple-300 ring-offset-1 sm:ring-offset-2' 
                      : respuestas[numero] 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300'}
                  `}
                  title={respuestas[numero] ? `Pregunta ${numero} - Respondida` : `Pregunta ${numero} - Sin responder`}
                >
                  {numero}
                </button>
              ))}
            </div>
            
            {/* Leyenda */}
            <div className="flex flex-wrap gap-2 sm:gap-4 mt-3 sm:mt-4 text-xs sm:text-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-green-500"></div>
                <span className="text-neutral-600">Respondida ({respondidas})</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-neutral-200"></div>
                <span className="text-neutral-600">Sin responder ({faltantes})</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-purple-600 ring-2 ring-purple-300"></div>
                <span className="text-neutral-600">Actual</span>
              </div>
            </div>

            {/* Listado de preguntas sin responder */}
            {faltantes > 0 && faltantes <= 10 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Preguntas sin responder: 
                  <span className="font-normal">
                    {preguntasSinResponder().join(', ')}
                  </span>
                </p>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Botón de enviar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="sticky bottom-2 sm:bottom-4 bg-white rounded-lg shadow-lg p-2 sm:p-4 border-2 border-neutral-200"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs sm:text-sm min-w-0">
              <p className="font-semibold text-neutral-800 truncate">
                <span className="hidden sm:inline">Preguntas respondidas: </span>{respondidas} / {TOTAL_PREGUNTAS}
              </p>
              {faltantes > 0 && (
                <p className="text-amber-600 truncate">Faltan {faltantes}</p>
              )}
            </div>
            <Button
              onClick={handleEnviarExamen}
              disabled={!validarRespuestasCompletas() || enviando}
              className="gap-1 sm:gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-xs sm:text-sm px-3 sm:px-4 flex-shrink-0"
              size="sm"
            >
              <Send className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{enviando ? 'Enviando...' : 'Finalizar Examen'}</span>
              <span className="sm:hidden">{enviando ? '...' : 'Finalizar'}</span>
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Modal de confirmación */}
      <Modal
        isOpen={showConfirmacion}
        onClose={() => setShowConfirmacion(false)}
        title="Confirmar envío"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">¿Estás seguro de finalizar el examen?</p>
              <p>Una vez enviado, no podrás modificar tus respuestas.</p>
            </div>
          </div>

          <div className="bg-neutral-50 rounded-lg p-4">
            <p className="text-sm text-neutral-700">
              <strong>Resumen:</strong>
            </p>
            <ul className="text-sm text-neutral-600 mt-2 space-y-1">
              <li>✓ Total de preguntas: {TOTAL_PREGUNTAS}</li>
              <li>✓ Respondidas: {respondidas}</li>
              <li>✓ Progreso: {progreso}%</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmacion(false)}
              className="flex-1"
            >
              Revisar respuestas
            </Button>
            <Button
              onClick={confirmarEnvio}
              disabled={enviando}
              className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
            >
              {enviando ? 'Enviando...' : 'Confirmar y enviar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Componente para cada pregunta individual
function PreguntaIPV({ numero, respuestaSeleccionada, onSeleccionar }) {
  const pregunta = PREGUNTAS_IPV.find(p => p.numero === numero);
  
  if (!pregunta) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(numero * 0.005, 0.5) }}
    >
      <Card className={`transition-all ${respuestaSeleccionada ? 'border-2 border-purple-300 bg-purple-50/30' : 'hover:shadow-md'}`}>
        <div className="p-3 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
            {/* Número de pregunta */}
            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${
              respuestaSeleccionada 
                ? 'bg-purple-600 text-white shadow-md' 
                : 'bg-neutral-200 text-neutral-700'
            }`}>
              {String(numero).padStart(2, '0')}
            </div>

            {/* Contenido de la pregunta */}
            <div className="flex-1 min-w-0">
              {/* Texto de la pregunta */}
              <div className="mb-3 sm:mb-4">
                <p className="text-neutral-800 leading-relaxed text-sm sm:text-base">
                  {pregunta.texto}
                  {respuestaSeleccionada && (
                    <CheckCircle className="w-5 h-5 text-green-600 inline-block ml-2 align-text-bottom" />
                  )}
                </p>
              </div>

              {/* Opciones */}
              <div className="space-y-2 sm:space-y-3">
                {pregunta.opciones.map((textoOpcion, index) => {
                  const letraOpcion = String.fromCharCode(65 + index); // A, B, C
                  const esSeleccionada = respuestaSeleccionada === letraOpcion;
                  
                  return (
                    <button
                      key={letraOpcion}
                      onClick={() => onSeleccionar(letraOpcion)}
                      className={`w-full p-3 sm:p-4 rounded-lg border-2 transition-all text-left group ${
                        esSeleccionada
                          ? 'border-purple-600 bg-purple-600 text-white shadow-md'
                          : 'border-neutral-300 bg-white text-neutral-700 hover:border-purple-400 hover:bg-purple-50 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        {/* Letra de la opción */}
                        <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm ${
                          esSeleccionada
                            ? 'bg-white text-purple-600'
                            : 'bg-purple-100 text-purple-700 group-hover:bg-purple-200'
                        }`}>
                          {letraOpcion}
                        </div>
                        
                        {/* Texto de la opción */}
                        <div className="flex-1 pt-0.5">
                          <p className={`text-xs sm:text-sm leading-relaxed ${esSeleccionada ? 'text-white' : 'text-neutral-700'}`}>
                            {textoOpcion}
                          </p>
                        </div>

                        {/* Indicador de selección */}
                        {esSeleccionada && (
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0 mt-0.5 sm:mt-1" fill="currentColor" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

// Pantalla de inicio
function PantallaInicio({ onIniciar }) {
  const [paso, setPaso] = useState(0); // 0: Instrucciones, 1: Confirmación final

  if (paso === 1) {
    // Pantalla de confirmación final
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full"
        >
          <Card className="border-t-4 border-t-purple-600">
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-purple-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-neutral-800 mb-4">
                ¡Estás Listo para Comenzar!
              </h2>
              
              <p className="text-lg text-neutral-600 mb-6">
                El test IPV consta de <strong>87 preguntas</strong> con 3 opciones cada una. Selecciona la que mejor te describa.
              </p>

              <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 mb-8">
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Recuerda:</strong> Responde con sinceridad sobre tus aptitudes
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Primera impresión:</strong> Confía en tu instinto inicial
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Tiempo estimado:</strong> 30-40 minutos aproximadamente
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300 mb-8">
                <p className="text-2xl font-bold text-green-800 mb-2">
                  ¡Mucha Suerte!
                </p>
                <p className="text-green-700">
                  Da clic en Continuar para comenzar el examen oficial
                </p>
              </div>

              <Button
                onClick={onIniciar}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg py-4"
              >
                <ChevronRight className="w-6 h-6 mr-2" />
                Continuar - Comenzar Examen Real
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Paso 0: Pantalla de instrucciones
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full"
      >
        <Card className="border-t-4 border-t-purple-600">
          <div className="p-8 text-center">
            <Brain className="w-16 h-16 text-purple-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-neutral-800 mb-2">
              Test IPV
            </h1>
            <p className="text-xl text-neutral-600 mb-6">
              Inventario de Personalidad para Vendedores
            </p>

            <div className="bg-purple-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Información del examen
              </h3>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Total de preguntas:</strong> 87</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Formato:</strong> 3 opciones (A, B, C) por pregunta</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Objetivo:</strong> Evaluar aptitudes para la venta</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Tiempo estimado:</strong> 30-40 minutos</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-blue-900 mb-3">📋 Instrucciones importantes</h3>
              <ul className="space-y-2 text-blue-800 text-sm">
                <li>✓ Responde con sinceridad, no hay respuestas correctas o incorrectas</li>
                <li>✓ Selecciona la opción que mejor te describa</li>
                <li>✓ No pienses demasiado cada respuesta, confía en tu primera impresión</li>
                <li>✓ Debes responder todas las preguntas para finalizar el examen</li>
                <li>✓ Una vez enviado, no podrás modificar tus respuestas</li>
              </ul>
            </div>

            <Button
              onClick={() => setPaso(1)}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg py-3"
            >
              Continuar
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

export default RealizarExamenIpv;
